import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Menu, X, Sparkles, Flame } from "lucide-react";
import { VoiceSelector } from "./VoiceSelector";
import { THEME_BUCKETS } from "../lib/lessonThemes";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [founderStatus, setFounderStatus] = useState(null);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    api.get("/fondateur/status").then((r) => setFounderStatus(r.data)).catch(() => {});
  }, []);

  const links = [
    { to: "/kana", label: "Kana" },
    { to: "/lessons", label: "Leçons" },
    { to: "/vocabulaire", label: "Vocabulaire" },
    { to: "/review", label: "Réviser" },
    { to: "/jlpt", label: "JLPT" },
    { to: "/tutor", label: "Sensei IA" },
    { to: "/blog", label: "Blog" },
    { to: "/pricing", label: "Tarifs" },
  ];

  return (
    <header
      className="sticky top-0 z-50 border-b border-[#1A2A40]/10 bg-[#FDFBF7]/85 backdrop-blur-xl"
      data-testid="navbar"
    >
      {/* Founder banner */}
      {founderStatus?.available && !user?.is_founder && !user?.is_lifetime && loc.pathname !== "/fondateur" && (
        <Link
          to="/fondateur"
          data-testid="founder-banner"
          className="block bg-[#1A2A40] text-white text-center py-2 text-xs font-bold tracking-wider hover:bg-black transition-colors"
        >
          <Flame className="w-3 h-3 inline mr-1.5 text-[#FFD166]" />
          OFFRE FONDATEUR · 49 € à vie pour les 100 premiers, puis 199 € ·
          <span className="underline ml-1.5">Voir l'offre →</span>
        </Link>
      )}
      <div className="max-w-7xl mx-auto px-6 sm:px-12 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link" aria-label="Japonais Pas à Pas, cours de japonais en ligne">
          <div className="w-10 h-10 bg-[#E94A3F] rounded-xl flex items-center justify-center shadow-brut-sm group-hover:rotate-6 transition-transform">
            <span className="font-jp text-white text-xl font-bold">日</span>
          </div>
          <div className="leading-tight">
            <div className="font-display font-black text-lg text-[#1A2A40]">Japonais</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#E94A3F] font-bold -mt-0.5">Pas à Pas</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, '-')}`}
              className={`text-sm font-medium hover:text-[#E94A3F] transition-colors ${loc.pathname === l.to ? 'text-[#E94A3F]' : 'text-[#1A2A40]'}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <VoiceSelector variant="compact" />
          {user ? (
            <>
              <Link
                to="/account"
                data-testid="account-link"
                className="text-sm font-bold text-[#1A2A40] hover:text-[#E94A3F]"
              >
                {user.name.split(" ")[0]} · {user.xp} XP
              </Link>
              <button
                onClick={() => { logout(); nav("/"); }}
                data-testid="logout-btn"
                className="text-sm font-medium text-[#4A5568] hover:text-[#E94A3F]"
              >
                Déconnexion
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="login-link" className="text-sm font-bold text-[#1A2A40] hover:text-[#E94A3F]">
                Connexion
              </Link>
              <Link
                to="/register"
                data-testid="register-cta"
                className="bg-[#E94A3F] text-white font-bold py-2.5 px-5 rounded-xl text-sm shadow-brut-sm hover-lift inline-flex items-center gap-1.5"
              >
                <Sparkles className="w-4 h-4" /> Essai gratuit 3j
              </Link>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2 rounded-lg hover:bg-[#F3F6F1]"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#1A2A40]/10 px-6 py-4 space-y-3 bg-[#FDFBF7]">
          <div className="pb-2 border-b border-[#1A2A40]/10 flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-widest text-[#A0AEC0]">Voix audio</span>
            <VoiceSelector variant="block" />
          </div>
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block py-2 font-medium text-[#1A2A40]">
              {l.label}
            </Link>
          ))}
          {user ? (
            <>
              <Link to="/account" onClick={() => setOpen(false)} className="block py-2 font-bold">Mon compte</Link>
              <Link to="/dashboard" onClick={() => setOpen(false)} className="block py-2 font-bold">Dashboard</Link>
              <button onClick={() => { logout(); nav("/"); setOpen(false); }} className="block py-2 text-[#4A5568]">Déconnexion</button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setOpen(false)} className="block py-2 font-bold">Connexion</Link>
              <Link to="/register" onClick={() => setOpen(false)} className="block py-2 bg-[#E94A3F] text-white text-center rounded-xl font-bold">
                Essai gratuit 3j
              </Link>
            </>
          )}
        </div>
      )}
    </header>
  );
};

export const Footer = () => (
  <footer className="border-t-2 border-[#1A2A40]/10 bg-[#F3F6F1] text-[#1A2A40] mt-20" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6 sm:px-12 py-16 grid grid-cols-2 md:grid-cols-5 gap-10">
      <div className="col-span-2">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-10 h-10 bg-[#E94A3F] rounded-xl flex items-center justify-center shadow-brut-sm">
            <span className="font-jp text-white text-xl font-bold">日</span>
          </div>
          <div className="leading-tight">
            <div className="font-display font-black text-lg">Japonais Pas à Pas</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#E94A3F] font-bold -mt-0.5">日本語、一歩ずつ</div>
          </div>
        </div>
        <p className="text-sm text-[#4A5568] max-w-md leading-relaxed">
          La méthode joyeuse et structurée pour apprendre le japonais sans s'épuiser.
          Conçue pour les francophones, propulsée par l'IA.
        </p>
      </div>
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-[#E94A3F] mb-3">Apprendre</div>
        <ul className="space-y-2 text-sm">
          <li><Link to="/kana" className="hover:text-[#E94A3F]">Hiragana &amp; Katakana</Link></li>
          <li><Link to="/lessons" className="hover:text-[#E94A3F]">Leçons</Link></li>
          <li><Link to="/vocabulaire" className="hover:text-[#E94A3F]">Vocabulaire</Link></li>
          <li><Link to="/placement" className="hover:text-[#E94A3F]">Test de niveau</Link></li>
          <li><Link to="/tutor" className="hover:text-[#E94A3F]">Sensei IA</Link></li>
          <li><Link to="/blog" className="hover:text-[#E94A3F]">Blog</Link></li>
          <li><Link to="/pricing" className="hover:text-[#E94A3F]">Tarifs</Link></li>
        </ul>
      </div>
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-[#E94A3F] mb-3">Guides</div>
        <ul className="space-y-2 text-sm">
          <li><Link to="/cours-de-japonais" className="hover:text-[#E94A3F]">Cours de japonais</Link></li>
          <li><Link to="/apprendre-le-japonais" className="hover:text-[#E94A3F]">Apprendre le japonais</Link></li>
          <li><Link to="/apprentissage-du-japonais" className="hover:text-[#E94A3F]">Apprentissage du japonais</Link></li>
        </ul>
      </div>
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-[#E94A3F] mb-3">Compte</div>
        <ul className="space-y-2 text-sm">
          <li><Link to="/register" className="hover:text-[#E94A3F]">S'inscrire</Link></li>
          <li><Link to="/login" className="hover:text-[#E94A3F]">Connexion</Link></li>
          <li><Link to="/dashboard" className="hover:text-[#E94A3F]">Mon espace</Link></li>
          <li><a href="mailto:alexis.gresoviac@gmail.com" data-testid="footer-contact-link" className="hover:text-[#E94A3F]">Contact</a></li>
        </ul>
      </div>
    </div>

    {/* Parcours par thème — 12 SEO landing pages, dense internal linking */}
    <div
      className="max-w-7xl mx-auto px-6 sm:px-12 pb-12 border-t border-[#1A2A40]/10 pt-10"
      data-testid="footer-themes"
    >
      <div className="text-xs font-bold uppercase tracking-widest text-[#E94A3F] mb-4">
        Parcours par thème
      </div>
      <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2 text-sm">
        {THEME_BUCKETS.map((b) => (
          <li key={b.slug}>
            <Link
              to={`/lessons/theme/${b.slug}`}
              data-testid={`footer-theme-${b.slug}`}
              className="inline-flex items-center gap-1.5 text-[#4A5568] hover:text-[#E94A3F] transition-colors"
            >
              <span aria-hidden="true">{b.emoji}</span>
              <span>{b.name}</span>
              <span className="text-[10px] text-[#A0AEC0] font-mono">({b.count})</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
    <div className="border-t border-[#1A2A40]/10 py-6 text-center text-xs text-[#A0AEC0]">
      © 2026 Japonais Pas à Pas, Gresoviac Management
    </div>
  </footer>
);
